import React, { useMemo } from "react";
import {
    Autocomplete,
    TextField,
} from "@mui/material";
import { useControlledAutocomplete } from "../hooks";

export type ControlledAutocompleteVariant =
    | "PROJECT"
    | "PORTFOLIO"
    | "PROGRAMME"
    | "SEARCH_PORTFOLIO_PROJECTS";

export interface AutocompleteOption {
    id: string | number;
    label?: string;
    project_reference?: string;
    [key: string]: any;
}

interface ControlledAutocompleteProps {
    type: ControlledAutocompleteVariant;
    value: AutocompleteOption | AutocompleteOption[] | null;
    label?: string;
    placeholder?: string;
    multiSelect?: boolean;
    onSelect: (
        value: AutocompleteOption | AutocompleteOption[] | null,
        type: ControlledAutocompleteVariant
    ) => void;
    disabled?: boolean;
    error?: boolean;
    helperText?: string | null;
}

export const ControlledAutocomplete: React.FC<ControlledAutocompleteProps> = ({
    value,
    label = "Search",
    placeholder = "Type to search...",
    type,
    multiSelect = false,
    onSelect,
    disabled = false,
    error = false,
    helperText,
}) => {

    const { options, loading, onChange, onInputChange } = useControlledAutocomplete(type);

    const getDisplayText = (option: AutocompleteOption) => {
        if (type === "PROJECT") return option.title || option.label || "";
        return option.description || option.name || option.label || "";
    };

    const structuredOptions = useMemo(() => {
        return [...options].sort((a, b) =>
            getDisplayText(a).toLowerCase().localeCompare(getDisplayText(b).toLowerCase())
        );
    }, [options, getDisplayText]);

    /** -------------------------------
     * SELECTION HANDLING
     * -------------------------------- */
    const handleSelect = (newValue: any) => {
        onSelect(newValue, type);
    };

    return (
        <Autocomplete
            disabled={disabled}
            multiple={multiSelect}
            disableCloseOnSelect={multiSelect}
            loading={loading}
            value={multiSelect ? (Array.isArray(value) ? value : []) : value}
            options={structuredOptions}
            onChange={(_, newValue) => {
                onChange(
                    type,
                    Array.isArray(newValue) ? newValue[0] ?? null : newValue
                );
                handleSelect(newValue);
            }}
            onInputChange={onInputChange}
            getOptionLabel={(option) => option.label || getDisplayText(option)}
            isOptionEqualToValue={(option, val) => option.id === val?.id}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    helperText={helperText}
                />
            )}
            sx={{ width: "100%" }}
        />
    );
};
